import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Users,
  IndianRupee,
  Sparkles,
  Check,
  Phone,
  Mail,
  Save,
  CheckCircle2,
  Settings,
  ShieldCheck
} from "lucide-react";
import { TEST_IDS } from "@/constants/testIds.js";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const AMENITY_OPTIONS = [
  "Air Conditioning / Central AC",
  "500kVA Generator / Power Backup",
  "Bridal & Groom Dressing Suites",
  "Grand Dining & Buffet Hall",
  "Stage & Ambient Lighting",
  "Surround Sound System",
  "Dedicated Valet & Parking Area",
  "Catering Kitchen Setup",
  "CCTV Security Surveillance",
  "Elevator & Wheelchair Access"
];

export default function BusinessSettingsPage({ businessInfo, onRefresh }) {
  const [formData, setFormData] = useState({
    business_name: "",
    category: "Convention Hall",
    tagline: "",
    contact_person: "",
    phone: "",
    email: "",
    address: "",
    city: "Bengaluru",
    state: "Karnataka",
    pincode: "560001",
    capacity_guests: 500,
    dining_capacity: 200,
    parking_capacity: 50,
    pricing_day_slot: 45000,
    pricing_night_slot: 60000,
    pricing_full_day: 95000,
    amenities: [],
    day_slot_timings: "06:00 AM - 04:59 PM",
    night_slot_timings: "05:00 PM - 11:59 PM",
    description: ""
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (businessInfo) {
      setFormData((prev) => ({
        ...prev,
        ...businessInfo,
        amenities: businessInfo.amenities || [
          "Air Conditioning / Central AC",
          "500kVA Generator / Power Backup",
          "Bridal & Groom Dressing Suites",
          "Grand Dining & Buffet Hall"
        ]
      }));
    }
  }, [businessInfo]);

  const handleToggleAmenity = (amenity) => {
    setFormData((prev) => {
      const exists = prev.amenities.includes(amenity);
      return {
        ...prev,
        amenities: exists
          ? prev.amenities.filter((a) => a !== amenity)
          : [...prev.amenities, amenity]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await axios.put(`${API}/business`, formData);
      setSaveSuccess(true);
      if (onRefresh) onRefresh();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      alert("Failed to save settings.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
      {/* Top Header with Back Navigation */}
      <div className="flex items-center justify-between">
        <Link
          to="/calendar"
          className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-pink-900 hover:text-pink-950 bg-white px-4 py-2 rounded-xl border border-pink-200 shadow-sm transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Calendar</span>
        </Link>

        <span className="text-xs font-semibold text-gray-500">
          Business Settings & Profile
        </span>
      </div>

      {/* Main Settings Card */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-xl overflow-hidden">
        {/* Banner */}
        <div className="bg-gradient-to-r from-pink-900 via-pink-800 to-rose-800 p-6 sm:p-8 text-white">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-xs font-semibold mb-2">
            <Settings className="w-3.5 h-3.5 text-pink-200" />
            <span>Business Settings</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
            Profile & Convention Hall Setup
          </h1>
          <p className="text-pink-100 text-xs sm:text-sm mt-1 max-w-xl">
            Configure your business profile, convention hall pricing, capacity, preset hours and venue amenities.
          </p>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6 bg-gray-50/30">
          
          {saveSuccess && (
            <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-2xl text-xs sm:text-sm text-emerald-900 flex items-center gap-2 font-bold animate-fade-in">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <span>Business settings and hall details saved successfully!</span>
            </div>
          )}

          {/* Section: General Info */}
          <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950 flex items-center gap-2 border-b border-pink-100 pb-3">
              <Building2 className="w-4 h-4 text-pink-700" />
              <span>General Venue Information</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Business / Hall Name *</label>
                <input
                  data-testid={TEST_IDS.setupBizNameInput}
                  type="text"
                  required
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  placeholder="e.g. Royal Palace Convention Hall"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Tagline / Subheading</label>
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                  placeholder="e.g. Premier Destination for Grand Weddings"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Contact Person / Owner *</label>
                <input
                  data-testid={TEST_IDS.setupContactPersonInput}
                  type="text"
                  required
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  placeholder="e.g. Rajesh Sharma"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                  <Phone className="w-3 h-3 text-pink-700" />
                  <span>Phone Number *</span>
                </label>
                <input
                  data-testid={TEST_IDS.setupPhoneInput}
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                  <Mail className="w-3 h-3 text-pink-700" />
                  <span>Email Address</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="bookings@royalhall.com"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-pink-700" />
                  <span>City & State</span>
                </label>
                <input
                  data-testid={TEST_IDS.setupCityInput}
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="e.g. Bengaluru, Karnataka"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Full Venue Address</label>
              <textarea
                data-testid={TEST_IDS.setupAddressInput}
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="45 Heritage Boulevard, Near Tech Park, Bengaluru 560068"
                className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm"
              />
            </div>
          </div>

          {/* Section: Capacity & Rates */}
          <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950 flex items-center gap-2 border-b border-pink-100 pb-3">
              <Users className="w-4 h-4 text-pink-700" />
              <span>Capacity & Default Slot Rates</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Guest Seating Capacity</label>
                <input
                  data-testid={TEST_IDS.setupCapacityGuestsInput}
                  type="number"
                  min="50"
                  max="10000"
                  value={formData.capacity_guests}
                  onChange={(e) => setFormData({ ...formData, capacity_guests: parseInt(e.target.value) || 0 })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm font-semibold"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Dining Capacity</label>
                <input
                  type="number"
                  value={formData.dining_capacity}
                  onChange={(e) => setFormData({ ...formData, dining_capacity: parseInt(e.target.value) || 0 })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Parking Space (Cars)</label>
                <input
                  type="number"
                  value={formData.parking_capacity}
                  onChange={(e) => setFormData({ ...formData, parking_capacity: parseInt(e.target.value) || 0 })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                  <IndianRupee className="w-3 h-3 text-pink-700" />
                  <span>Day Function Rate (₹)</span>
                </label>
                <input
                  data-testid={TEST_IDS.setupDaySlotRateInput}
                  type="number"
                  value={formData.pricing_day_slot}
                  onChange={(e) => setFormData({ ...formData, pricing_day_slot: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm font-bold text-pink-950"
                />
                <span className="text-[10px] text-gray-500 mt-1 block">Preset: Until 04:59 PM</span>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                  <IndianRupee className="w-3 h-3 text-pink-700" />
                  <span>Night Function Rate (₹)</span>
                </label>
                <input
                  data-testid={TEST_IDS.setupNightSlotRateInput}
                  type="number"
                  value={formData.pricing_night_slot}
                  onChange={(e) => setFormData({ ...formData, pricing_night_slot: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm font-bold text-pink-950"
                />
                <span className="text-[10px] text-gray-500 mt-1 block">Preset: 05:00 PM - 11:59 PM</span>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
                  <IndianRupee className="w-3 h-3 text-pink-700" />
                  <span>Full Day (24h) Rate (₹)</span>
                </label>
                <input
                  data-testid={TEST_IDS.setupFullDayRateInput}
                  type="number"
                  value={formData.pricing_full_day}
                  onChange={(e) => setFormData({ ...formData, pricing_full_day: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm font-bold text-pink-950"
                />
                <span className="text-[10px] text-gray-500 mt-1 block">24 Hours / Multi-Day base</span>
              </div>
            </div>
          </div>

          {/* Section: Amenities */}
          <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950 flex items-center gap-2 border-b border-pink-100 pb-3">
              <Sparkles className="w-4 h-4 text-pink-700" />
              <span>Hall Amenities & Features</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {AMENITY_OPTIONS.map((item) => {
                const isChecked = formData.amenities.includes(item);
                return (
                  <label
                    key={item}
                    onClick={() => handleToggleAmenity(item)}
                    className={`flex items-center gap-3 p-3 rounded-2xl border cursor-pointer text-xs font-semibold transition-all ${
                      isChecked
                        ? "bg-pink-50 border-pink-400 text-pink-950"
                        : "bg-white border-gray-200 text-gray-600 hover:border-pink-200"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded flex items-center justify-center border ${
                        isChecked ? "bg-pink-800 border-pink-800 text-white" : "border-gray-300"
                      }`}
                    >
                      {isChecked && <Check className="w-3 h-3" />}
                    </div>
                    <span>{item}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Section: Description */}
          <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm space-y-2">
            <label className="block text-xs font-bold text-pink-950 uppercase tracking-wide">
              Venue Description & Policies
            </label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Highlight special features, catering policies, caution deposit terms, etc."
              className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm"
            />
          </div>

          {/* Submit Actions */}
          <div className="pt-4 border-t border-pink-100 flex items-center justify-end gap-3">
            <Link
              to="/calendar"
              className="px-5 py-3 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-100 text-sm font-semibold transition-colors"
            >
              Cancel
            </Link>
            <button
              data-testid={TEST_IDS.setupSaveBtn}
              type="submit"
              disabled={isSaving}
              className="px-8 py-3 rounded-xl bg-gradient-to-r from-pink-900 via-pink-800 to-rose-700 text-white font-bold text-sm shadow-lg hover:opacity-95 active:scale-95 transition-all flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              <span>{isSaving ? "Saving..." : "Save Settings & Hall Setup"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
